import React, { useRef, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import Typewriter from "typewriter-effect";
import { Bio } from "../../data/constanit";
import HeroBgAnimation from "./heroSectionAnimation/index";
import AmbientHeroCanvas from "../3d/AmbientHeroCanvas";
import { Download, Mail, ExternalLink, MapPin } from "lucide-react";
import { FaGithub as Github, FaLinkedin as Linkedin } from "react-icons/fa";
import photo from "../../img/Vivek.png";
const HeroContainer = styled.section`
  position: relative;
  min-height: 88vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 100px 24px 60px;
  overflow: hidden;
  z-index: 1;

  @media (max-width: 960px) {
    padding: 90px 20px 50px;
    min-height: auto;
  }
`;

const BackgroundLayer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1200px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 40px;

  @media (max-width: 960px) {
    flex-direction: column-reverse;
    text-align: center;
    gap: 36px;
  }
`;

const HeroLeft = styled.div`
  flex: 1.2;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 960px) {
    align-items: center;
  }
`;

const LocationBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  background: ${({ theme }) => `${theme.primary}12`};
  border: 1px solid ${({ theme }) => `${theme.primary}33`};
  color: ${({ theme }) => theme.primary};
  font-size: 13px;
  font-weight: 600;
  width: fit-content;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 10px #10b981;
  display: inline-block;
`;

const Title = styled(motion.h1)`
  font-size: 52px;
  font-weight: 800;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.18;
  letter-spacing: -0.5px;

  span.highlight {
    background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, ${({ theme }) => theme.secondary} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
  }

  @media (max-width: 960px) {
    font-size: 40px;
  }

  @media (max-width: 540px) {
    font-size: 32px;
  }
`;

const TextLoop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_secondary};
  min-height: 40px;

  span.dynamic {
    color: ${({ theme }) => theme.primary};
    font-weight: 700;
  }

  @media (max-width: 960px) {
    justify-content: center;
    font-size: 20px;
  }

  @media (max-width: 540px) {
    font-size: 17px;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 16.5px;
  line-height: 1.75;
  color: ${({ theme }) => theme.text_secondary};
  max-width: 600px;

  @media (max-width: 960px) {
    font-size: 15px;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 10px;

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 26px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, #0284c7 100%);
  color: #030712;
  font-size: 14.5px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 4px 18px ${({ theme }) => `${theme.primary}40`};
  transition: all 0.25s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 24px ${({ theme }) => `${theme.primary}60`};
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 22px;
  border-radius: 12px;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_primary};
  font-size: 14.5px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.25s ease;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }
`;

const SocialLinksRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

const SocialIconLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_secondary};
  display: grid;
  place-items: center;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.primary};
    border-color: ${({ theme }) => theme.primary};
    transform: translateY(-2px);
  }
`;

const HeroRight = styled.div`
  flex: 0.8;
  display: flex;
  justify-content: center;
  perspective: 1000px;
`;

const PhotoCard = styled(motion.div)`
  position: relative;
  width: 320px;
  border-radius: 28px;
  padding: 10px;
  background: ${({ theme }) => `linear-gradient(145deg, ${theme.card_light} 0%, ${theme.bgSoft} 100%)`};
  border: 1px solid ${({ theme }) => `${theme.primary}33`};
  box-shadow: 0 24px 50px rgba(0, 0, 0, 0.5), 0 0 35px ${({ theme }) => `${theme.primary}1a`};
  transition: transform 0.2s ease-out, box-shadow 0.2s ease-out;
  cursor: grab;

  @media (max-width: 640px) {
    width: 260px;
  }
`;

const Photo = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 20px;
  display: block;
`;

const PhotoMetaBadge = styled.div`
  margin-top: 10px;
  padding: 10px 14px;
  border-radius: 14px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
`;

const HeroSection = () => {
  const cardRef = useRef(null);
  const [rotate, setRotate] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    setRotate({
      x: ((y - centerY) / centerY) * -10,
      y: ((x - centerX) / centerX) * 10
    });
  };

  const handleMouseLeave = () => {
    setRotate({ x: 0, y: 0 });
  };

  return (
    <HeroContainer id="about">
      {/* Ambient 3D Starfield & Background Wave */}
      <BackgroundLayer>
        <AmbientHeroCanvas />
        <HeroBgAnimation />
      </BackgroundLayer>

      <HeroInner>
        <HeroLeft>
          <LocationBadge
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <StatusDot />
            <MapPin size={13} />
            <span>Indore, MP, India • MERN Stack Developer</span>
          </LocationBadge>

          <Title
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
          >
            Hi, I am <br />
            <span className="highlight">{Bio.name}</span>
          </Title>

          <TextLoop>
            I am a
            <span className="dynamic">
              <Typewriter
                options={{
                  strings: Bio.roles,
                  autoStart: true,
                  loop: true,
                  delay: 45,
                  deleteSpeed: 25
                }}
              />
            </span>
          </TextLoop>

          <Subtitle
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2 }}
          >
            {Bio.summary}
          </Subtitle>

          <ButtonGroup
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.3 }}
          >
            <PrimaryButton href="#projects">
              View Projects <ExternalLink size={15} />
            </PrimaryButton>

            <SecondaryButton href={Bio.resume} target="_blank" rel="noopener noreferrer">
              <Download size={15} /> Check Resume
            </SecondaryButton>

            <SecondaryButton href="#contact">
              <Mail size={15} /> Contact Me
            </SecondaryButton>
          </ButtonGroup>

          <SocialLinksRow>
            <SocialIconLink
              href={Bio.github}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub Profile"
            >
              <Github size={17} />
            </SocialIconLink>
            <SocialIconLink
              href={Bio.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn Profile"
            >
              <Linkedin size={17} />
            </SocialIconLink>
            <SocialIconLink
              href={`mailto:${Bio.email}`}
              aria-label="Send Direct Email"
            >
              <Mail size={17} />
            </SocialIconLink>
          </SocialLinksRow>
        </HeroLeft>

        <HeroRight>
          <PhotoCard
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{
              opacity: 1,
              scale: 1,
              rotateX: rotate.x,
              rotateY: rotate.y
            }}
            transition={{
              scale: { duration: 0.6, delay: 0.15 },
              opacity: { duration: 0.6, delay: 0.15 },
              rotateX: { type: "spring", stiffness: 350, damping: 25 },
              rotateY: { type: "spring", stiffness: 350, damping: 25 }
            }}
          >
            <Photo
              src={photo}
              alt="Vivek Carpenter"
            />
            <PhotoMetaBadge>
              <span style={{ color: "#38bdf8" }}>1.6+ Years Exp</span>
              <span style={{ color: "#94a3b8" }}>React • Node • Cloud</span>
            </PhotoMetaBadge>
          </PhotoCard>
        </HeroRight>
      </HeroInner>
    </HeroContainer>
  );
};

export default HeroSection;
